import { RUM_MAX_SELECTOR_LENGTH } from './constants'

import type { RumLoadState } from './types'

const MAX_SELECTOR_DEPTH = 5

const MAX_CLASSES_PER_ELEMENT = 3

export function clampToZero(value: number): number {
    if (!Number.isFinite(value) || value < 0) return 0

    return value
}

export function toMs(value: number | undefined): number | undefined {
    if (typeof value !== 'number' || !Number.isFinite(value)) return undefined

    return Math.round(value)
}

function getEntriesByType(type: string): PerformanceEntry[] {
    try {
        if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') return []

        return performance.getEntriesByType(type)
    } catch {
        return []
    }
}

export function getNavigationEntry(): PerformanceNavigationTiming | undefined {
    return getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
}

export function getResourceEntry(url: string | undefined): PerformanceResourceTiming | undefined {
    if (!url) return undefined

    const entries = getEntriesByType('resource') as PerformanceResourceTiming[]

    return entries.find((entry) => entry.name === url)
}

export function getLoadState(timestamp: number): RumLoadState {
    const navigation = getNavigationEntry()

    if (!navigation) return 'loading'
    if (timestamp < navigation.domInteractive) return 'loading'
    if (navigation.domContentLoadedEventStart === 0 || timestamp < navigation.domContentLoadedEventStart) {
        return 'dom-interactive'
    }
    if (navigation.domComplete === 0 || timestamp < navigation.domComplete) return 'dom-content-loaded'

    return 'complete'
}

function describeElement(element: Element): string {
    const tag = element.nodeName.toLowerCase()

    if (element.id) return `${tag}#${element.id}`

    const classes = (element.getAttribute('class') ?? '').split(/\s+/).filter(Boolean).slice(0, MAX_CLASSES_PER_ELEMENT)

    return classes.length > 0 ? `${tag}.${classes.join('.')}` : tag
}

export function getSelector(node: Node | null | undefined): string | undefined {
    let element = node instanceof Element ? node : (node?.parentElement ?? null)
    const parts: string[] = []

    while (element && parts.length < MAX_SELECTOR_DEPTH) {
        parts.unshift(describeElement(element))

        if (element.id) break

        element = element.parentElement
    }

    if (parts.length === 0) return undefined

    return parts.join('>').slice(0, RUM_MAX_SELECTOR_LENGTH)
}
